import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarBlank, Fire, HandCoins, LinkSimple, MapPin, ShareNetwork, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TipModal from "../components/TipModal";

export default function PromoterProfile() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get("tip") === "1" && data?.promoter) setTipOpen(true);
  }, [searchParams, data]);

  const openTip = () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setTipOpen(true);
  };

  useEffect(() => {
    if (!handle || !handle.startsWith("@")) {
      navigate("/", { replace: true });
      return;
    }
    API.get(`/promoters/handle/${encodeURIComponent(handle)}`)
      .then((r) => {
        setData(r.data);
        if (r.data?.promoter?.referral_code) {
          localStorage.setItem("np_ref", r.data.promoter.referral_code);
        }
      })
      .catch(() => setNotFound(true));
  }, [handle, navigate]);

  if (notFound) {
    return (
      <div data-testid="promoter-not-found" className="flex min-h-screen flex-col items-center justify-center bg-black px-5 text-center text-white">
        <img src="/logo.png" alt="NITEPASS" className="h-12 w-auto" />
        <p className="mt-6 font-display text-xl font-black">Promoter not found</p>
        <p className="mt-2 text-sm text-zinc-500">This NITEPASS handle doesn't exist yet.</p>
        <button data-testid="promoter-notfound-home-btn" onClick={() => navigate("/")}
          className="mt-6 rounded-full bg-neon-gold px-6 py-2.5 text-xs font-black text-black">Back to NITEPASS</button>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-black px-5 pt-20 text-center text-sm text-zinc-500">Loading promoter...</div>;
  const p = data.promoter;

  return (
    <div data-testid="promoter-profile-page" className="min-h-screen bg-black pb-16 text-white">
      <div className="mx-auto max-w-3xl px-5 pt-6" style={{ paddingTop: "calc(1.5rem + env(safe-area-inset-top))" }}>
        <div className="flex items-center justify-between">
          <button data-testid="promoter-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs font-bold text-zinc-500">
            <ArrowLeft size={14} /> Back
          </button>
          <a data-testid="promoter-share-card-btn"
            href={mediaUrl(`/api/promoters/handle/${encodeURIComponent(handle)}/card.png`)}
            className="flex items-center gap-1.5 rounded-full border border-neon-gold/40 px-3.5 py-1.5 text-[11px] font-bold text-neon-gold">
            <ShareNetwork size={13} weight="bold" /> Share Card
          </a>
          <button data-testid="promoter-copy-link-btn"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(`${window.location.origin}/api/share/promoter/${encodeURIComponent(handle)}`);
                toast.success("Share link copied — unfurls with your card");
              } catch { toast.error("Copy failed"); }
            }}
            className="flex items-center gap-1.5 rounded-full border border-neon-violet/40 px-3.5 py-1.5 text-[11px] font-bold text-neon-violet">
            <LinkSimple size={13} weight="bold" /> Copy Link
          </button>
        </div>
        <section className="relative mt-5 overflow-hidden rounded-[2rem] border border-neon-violet/40 bg-[#0b0612] p-6">
          <div className="absolute -right-14 -top-14 h-48 w-48 rounded-full bg-neon-violet/25 blur-3xl" />
          <div className="relative flex items-start gap-5">
            <div className="h-24 w-24 shrink-0 overflow-hidden rounded-3xl border-2 border-neon-gold bg-neon-gold/10 shadow-glow-gold">
              {p.avatar_url ? (
                <img src={mediaUrl(p.avatar_url)} alt={p.name} className="h-full w-full object-cover" data-testid="promoter-avatar" />
              ) : (
                <div className="flex h-full w-full items-center justify-center font-display text-3xl font-black text-neon-gold">{p.name?.[0]}</div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="inline-flex rounded-full border border-neon-gold/40 bg-neon-gold/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-neon-gold" data-testid="promoter-title-chip">
                {p.title}
              </div>
              <h1 className="mt-3 font-display text-3xl font-black">{p.name}</h1>
              <div className="mt-1 text-sm font-bold text-neon-violet">{p.handle}</div>
              {p.bio && <p className="mt-4 text-sm leading-relaxed text-zinc-300" data-testid="promoter-bio">{p.bio}</p>}
            </div>
          </div>
          <div className="relative mt-6 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-2xl bg-white/5 p-3"><div className="font-display text-2xl font-black text-neon-yellow">{p.score ?? "—"}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Score</div></div>
            <div className="rounded-2xl bg-white/5 p-3"><div className="font-display text-2xl font-black text-neon-green">{p.verified_guests}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Guests</div></div>
            <div className="rounded-2xl bg-white/5 p-3"><div className="font-display text-2xl font-black text-neon-violet">{p.events_count}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Events</div></div>
          </div>
        </section>

        <section className="mt-8">
          <h2 className="flex items-center gap-2 font-display text-lg font-black"><Fire size={18} className="text-neon-gold" /> Upcoming Events</h2>
          <div className="mt-4 space-y-3" data-testid="promoter-events-list">
            {data.events.map((event) => (
              <div key={event.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-base font-black">{event.title}</div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-zinc-500">
                      <span className="flex items-center gap-1"><CalendarBlank size={12} /> {event.date_label} · {event.start}{event.end ? `–${event.end}` : ""}</span>
                      {event.venue_name && <span className="flex items-center gap-1"><MapPin size={12} /> {event.venue_name}</span>}
                    </div>
                    {event.specials && <div className="mt-2 text-[11px] font-bold text-neon-gold">{event.specials}</div>}
                  </div>
                  {event.venue_id && (
                    <button data-testid={`promoter-event-venue-${event.id}`} onClick={() => navigate(`/venue/${event.venue_id}`)}
                      className="rounded-full border border-neon-violet/40 px-3 py-1.5 text-[11px] font-bold text-neon-violet">Venue</button>
                  )}
                </div>
              </div>
            ))}
            {data.events.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">No published events yet.</p>}
          </div>
        </section>

        <div className="mt-8 rounded-2xl border border-neon-gold/20 bg-neon-gold/5 p-4 text-xs text-zinc-400">
          <Star size={14} className="mr-1 inline text-neon-gold" /> Promoter score is based on verified scans and event performance.
        </div>

        <button data-testid="promoter-tip-btn" onClick={openTip}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-neon-gold/50 bg-neon-gold/10 py-3 text-sm font-black text-neon-gold transition-transform duration-200 hover:scale-[1.02]">
          <HandCoins size={18} weight="fill" /> Tip {p?.name?.split(" ")[0]} — 100% goes to them
        </button>

        <button data-testid="promoter-get-pass-btn" onClick={() => navigate("/auth?mode=register")}
          className="mt-4 w-full rounded-full bg-neon-gold py-3.5 text-sm font-black text-black shadow-glow-gold transition-transform duration-200 hover:scale-[1.02]">
          Get the Pass — Join {p?.name?.split(" ")[0]}'s Guest List
        </button>
        <TipModal promoter={p} open={tipOpen} onClose={() => setTipOpen(false)} />
      </div>
    </div>
  );
}
